import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ConditionConfigurationModel } from '@fleet/model';
import { Subject, Subscription } from 'rxjs';

import { ToggleWithLeftLabelAndDescriptionComponent } from '@fleet/ui/components/toggles/toggle-with-left-label-and-description/toggle-with-left-label-and-description.component';
import { NetworkConditionsService } from '../network-conditions.service';

@Component({
  selector: 'fleet-network-conditions-selector',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatListModule,
    MatProgressSpinnerModule,
    ToggleWithLeftLabelAndDescriptionComponent,
  ],
  templateUrl: './network-conditions-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NetworkConditionsSelectorComponent),
      multi: true,
    },
  ],
})
export class NetworkConditionsSelectorComponent
  implements OnInit, OnChanges, ControlValueAccessor, OnDestroy
{
  @Input() conditionsLabel: string = null;
  driverConditions: ConditionConfigurationModel[] = [];
  vehicleConditions: ConditionConfigurationModel[] = [];
  travellerConditions: ConditionConfigurationModel[] = [];
  jobConditions: ConditionConfigurationModel[] = [];
  organisationConditions: ConditionConfigurationModel[] = [];
  medallionConditions: ConditionConfigurationModel[] = [];

  allConditions: ConditionConfigurationModel[] = [];

  currentEntityConditionIds: string[] = [];

  conditionsFormArray: UntypedFormArray = new UntypedFormArray([]);

  _unsubscribeAll: Subject<any> = new Subject();

  /** Value-change subscriptions for `mode === 'toggle'` (no template `(change)` on fleet toggle). */
  private toggleModeSubs = new Subscription();

  @Input() filter: string;
  formControl = new UntypedFormControl();
  @Input() mode: string;
  @Input() valueMode: string;
  @Input() columns = '2';
  @Input() multiSelect = true;

  @Output() addCondition = new EventEmitter();
  @Output() removeCondition = new EventEmitter();
  @ViewChild('conditionSelectionList') conditionSelectionList: MatSelectionList;

  selectedConditions: ConditionConfigurationModel[] = [];
  /** True once condition configs for the current `filter` have been loaded from the service. */
  conditionsLoaded = signal(false);
  /** Filter types whose condition config HTTP response has been applied (e.g. DRIVER, VEHICLE). */
  resolvedFilters = signal<Set<string>>(new Set());
  /** Network condition IDs with an in-flight add/remove API call from the parent. */
  processingConditionIds = signal<Set<string>>(new Set());

  constructor(
    private networkConditionsService: NetworkConditionsService,
    public changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.networkConditionsService.driverConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.driverConditions.length === 0 && c) {
          this.driverConditions = c;
          this.resolvedFilters.update((s) => new Set(s).add('DRIVER'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('DRIVER');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.vehicleConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.vehicleConditions.length === 0 && c) {
          this.vehicleConditions = c;
          this.resolvedFilters.update((s) => new Set(s).add('VEHICLE'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('VEHICLE');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.travellerConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.travellerConditions.length === 0 && c) {
          this.travellerConditions = c;
          this.resolvedFilters.update((s) => new Set(s).add('TRAVELLER'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('TRAVELLER');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.jobConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.jobConditions.length === 0 && c) {
          this.jobConditions = c;
          this.resolvedFilters.update((s) => new Set(s).add('BOOKING'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('BOOKING');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.organisationConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.organisationConditions.length === 0 && c) {
          this.organisationConditions = c;
          this.resolvedFilters.update((s) => new Set(s).add('ORGANISATION'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('ORGANISATION');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.medallionConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.medallionConditions.length === 0 && c) {
          this.medallionConditions = c;
          this.resolvedFilters.update((s) => new Set(s).add('MEDALLION'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('MEDALLION');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.formControl.valueChanges.subscribe(
      (value: ConditionConfigurationModel[]) => {
        //this should onchnage array of ids
        //used in job, driver ,vehicle search and manula dispatch

        //you can check that write value is working in search context by running
        //WhenCreatingJobWithConditions and then opening manual dispath at job/dispatch/{{jobId}}
        //the job.jobDetails.conditions needs to be mapped to array of ids and form control value in manual dispatch needs to be patch
        this.onTouched();
        this.onChange(value);
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      if (!changes['filter'].firstChange) {
        this.conditionsLoaded.set(false);
      }
      this.syncConditionsLoadedWithFilter();
    }
    if (changes['mode']) {
      if (this.mode === 'toggle') {
        this.refreshToggleModeSubscriptions();
      } else {
        this.toggleModeSubs.unsubscribe();
        this.toggleModeSubs = new Subscription();
      }
    }
  }

  /** If `filter` is bound after configs already arrived, still mark loaded. */
  private syncConditionsLoadedWithFilter(): void {
    if (!this.filter) {
      return;
    }
    if (this.resolvedFilters().has(this.filter)) {
      this.conditionsLoaded.set(true);
    }
  }

  markConditionProcessing(networkConditionId: string): void {
    this.processingConditionIds.update((s) => {
      const n = new Set(s);
      n.add(networkConditionId);
      return n;
    });
  }

  clearConditionProcessing(networkConditionId: string): void {
    this.processingConditionIds.update((s) => {
      const n = new Set(s);
      n.delete(networkConditionId);
      return n;
    });
  }

  isConditionProcessing(networkConditionId: string): boolean {
    return this.processingConditionIds().has(networkConditionId);
  }

  onChange: any = (condition: any) => {};
  onTouched: any = () => {};
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable({ emitEvent: false });
    } else {
      this.formControl.enable({ emitEvent: false });
    }
  }

  //this should be array of Ids
  writeValue(conditionIds: string[]) {
    if (conditionIds) {
      //currentEntityConditions is array of ids

      if (typeof conditionIds === 'string') {
        //single id
        this.currentEntityConditionIds = [conditionIds];
      } else {
        this.currentEntityConditionIds = conditionIds;
      }

      this.formControl.setValue(this.currentEntityConditionIds, {
        emitEvent: false,
      });

      if (this.filter === 'DRIVER') {
        this.conditionsFormArray.setValue(
          this.driverConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (conditionIds.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );
      } else if (this.filter === 'VEHICLE') {
        this.conditionsFormArray.setValue(
          this.vehicleConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (conditionIds.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );

        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      } else if (this.filter === 'TRAVELLER') {
        this.conditionsFormArray.setValue(
          this.travellerConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (conditionIds.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );
      } else if (this.filter === 'BOOKING') {
        this.conditionsFormArray.setValue(
          this.jobConditions.map((condition: ConditionConfigurationModel) => {
            if (conditionIds.includes(condition.networkConditionId)) {
              return true;
            } else {
              return false;
            }
          }),
          { emitEvent: false }
        );
      } else if (this.filter === 'ORGANISATION') {
        this.conditionsFormArray.setValue(
          this.organisationConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (conditionIds.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      } else if (this.filter === 'MEDALLION') {
        this.conditionsFormArray.setValue(
          this.medallionConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (conditionIds.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );

        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      } else {
        this.conditionsFormArray.setValue(
          this.allConditions.map((condition: ConditionConfigurationModel) => {
            if (conditionIds.includes(condition.networkConditionId)) {
              return true;
            } else {
              return false;
            }
          }),
          { emitEvent: false }
        );
      }
    }
  }
  ngOnDestroy(): void {
    this.toggleModeSubs.unsubscribe();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  syncConditionsAndFormArray(): void {
    if (this.filter === 'DRIVER') {
      this.conditionsFormArray = new UntypedFormArray(
        this.driverConditions.map(
          (condition) =>
            new UntypedFormControl({
              value: null,
              disabled: condition.assignmentMode === 'CERTIFICATE',
            })
        )
      );
    } else if (this.filter === 'VEHICLE') {
      this.conditionsFormArray = new UntypedFormArray(
        this.vehicleConditions.map((condition) => new UntypedFormControl())
      );
    } else if (this.filter === 'TRAVELLER') {
      this.conditionsFormArray = new UntypedFormArray(
        this.travellerConditions.map((condition) => new UntypedFormControl())
      );
    } else if (this.filter === 'BOOKING') {
      this.conditionsFormArray = new UntypedFormArray(
        this.jobConditions.map((condition) => new UntypedFormControl())
      );
    } else if (this.filter === 'ORGANISATION') {
      this.conditionsFormArray = new UntypedFormArray(
        this.organisationConditions.map(
          (condition) =>
            new UntypedFormControl({
              value: null,
              disabled: condition.assignmentMode === 'CERTIFICATE',
            })
        )
      );
    } else if (this.filter === 'MEDALLION') {
      this.conditionsFormArray = new UntypedFormArray(
        this.medallionConditions.map((condition) => new UntypedFormControl())
      );
    }

    // Reapply entity selection if writeValue ran before configs were available.
    this.writeValue(this.currentEntityConditionIds);
    this.refreshToggleModeSubscriptions();
    this.changeDetectorRef.markForCheck();
  }

  private markConditionsLoadedForFilter(loadedType: string): void {
    if (this.filter === loadedType) {
      this.conditionsLoaded.set(true);
    }
  }

  toggleCondition(condition: ConditionConfigurationModel, checked: boolean) {
    if (!checked) {
      this.removeCondition.emit(condition.networkConditionId);
    } else {
      this.addCondition.emit(condition.networkConditionId);
    }
  }

  private getConditionsForCurrentFilter(): ConditionConfigurationModel[] {
    if (this.filter === 'VEHICLE') {
      return this.vehicleConditions;
    }
    if (this.filter === 'DRIVER') {
      return this.driverConditions;
    }
    if (this.filter === 'TRAVELLER') {
      return this.travellerConditions;
    }
    if (this.filter === 'BOOKING') {
      return this.jobConditions;
    }
    if (this.filter === 'ORGANISATION') {
      return this.organisationConditions;
    }
    if (this.filter === 'MEDALLION') {
      return this.medallionConditions;
    }
    return [];
  }

  private refreshToggleModeSubscriptions(): void {
    this.toggleModeSubs.unsubscribe();
    this.toggleModeSubs = new Subscription();

    if (this.mode !== 'toggle') {
      return;
    }

    const conditions = this.getConditionsForCurrentFilter();
    if (!conditions?.length || !this.conditionsFormArray?.length) {
      return;
    }

    conditions.forEach((condition, i) => {
      const ctrl = this.conditionsFormArray.at(i);
      if (!ctrl) {
        return;
      }
      const sub = ctrl.valueChanges.subscribe((checked: boolean) => {
        this.toggleCondition(condition, checked);
      });
      this.toggleModeSubs.add(sub);
    });
  }
}
